import React from 'react';
import {useGetSubOrganizationsQuery} from "../../store/Api";
import {useSelector} from "react-redux";
import {RootState} from "../../store/rootReducer";
import {
    CommandBar,
    IColumn,
    ICommandBarItemProps,
    Image,
    ImageFit,
    SelectionMode,
    ShimmeredDetailsList
} from "@fluentui/react";
import {ButtonColumnGenerator, ColumnGenerator} from "../../logic/Columns/ColumnGenerator";
import {ISubOrganization} from "../../models/Settings";
import SubOrganizationModal from "../../components/SubOrganizationModal/SubOrganizationModal";
import {useGetOrganizationEntitlementsQuery} from "../../store/endpoints/OrganizationsEndpoints";
import { useUrlPanelState } from '../../hooks/useUrlState';

const SubOrganizationManagement: React.FC = () => {
    const organization = useSelector((state: RootState) => state.settings.selectedOrganization);
    const getOrgEntitlements = useGetOrganizationEntitlementsQuery(organization ?? '', {skip: !organization || organization === ''});
    const getSubOrganizations = useGetSubOrganizationsQuery();
    /*
     * Editing an existing record goes through the URL (?panel=subOrganization&instance=<id>) so a link
     * can open it; the add flow has nothing to link to, so it stays local.
     */
    const subOrganizationPanel = useUrlPanelState('subOrganization');
    const [creating, setCreating] = React.useState(false);
    const selectedSubOrg = creating ? undefined : getSubOrganizations.data?.find((s: ISubOrganization) => s.id === subOrganizationPanel.instanceId);
    const modalOpen = subOrganizationPanel.isOpen && (creating || !!selectedSubOrg);

    if (getOrgEntitlements.data?.subOrganizationsEnabled !== true) {
        return (
            <div>
                <h1>Sub Organization Management</h1>
                <p>Sub Organizations are not enabled for this organization.</p>
            </div>
        );
    }

    const commands: ICommandBarItemProps[] = [
        {
            key: 'new',
            text: 'Add',
            iconProps: { iconName: 'Add' },
            onClick: () => {setCreating(true); subOrganizationPanel.open();}
        }
    ];

    const farCommands: ICommandBarItemProps[] = [{
        key: 'refresh',
        text: 'Refresh',
        iconOnly: true,
        iconProps: { iconName: 'Refresh' },
        onClick: () => {getSubOrganizations.refetch();}
    }];

    const columns: IColumn[] = [
        ColumnGenerator('Name', 'name', 100),
        ColumnGenerator('Country', 'country', 100, 100),
        ColumnGenerator('Logo', 'logo', 50, 50, (item: ISubOrganization) => (
            <Image src={item.logo} width={50} height={50} imageFit={ImageFit.contain} />
        )),
        ButtonColumnGenerator((s: ISubOrganization) => {setCreating(false); subOrganizationPanel.open(s.id);}, '', 'Details'),
    ]

    return (
        <div>
            <h1>Sub Organization Management</h1>
            <CommandBar items={commands} farItems={farCommands} />
            <ShimmeredDetailsList
                selectionMode={SelectionMode.none}
                items={getSubOrganizations.data ?? []}
                enableShimmer={getSubOrganizations.isFetching}
                columns={columns}
            />
            <SubOrganizationModal
                isOpen={modalOpen}
                onClose={() => {setCreating(false); subOrganizationPanel.close();}}
                subOrganization={selectedSubOrg}
            />
        </div>
    );
}

export default SubOrganizationManagement;